import React, { useEffect, useState } from 'react';
import LeftIcon from '../../images/LeftIcon';
import ArrowRightIcon from '../../images/ArrowRightIcon';
import { webLoadingFalse, webLoadingTrue, webNavigate } from '../../services/navigate.service';
import PopupLocation from '../../components/PopupLocation';
import {
  createGroupProduct,
  getDetailProductGroup,
  updateGroupProduct,
} from '../../services/items.services';
import { validateOnlyNumbers } from '../../validation/ValidationInput';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { inventoryFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from '../../services/fieldLimits';
import FieldCounter from '../../components/FieldCounter';
import BackIcon from '../../images/BackIcon';
import { useResponsive } from '../../hook/useResponsive';
import { useFormKeyboard } from '../../hook/useFormKeyboard';

interface TermsResponse {
  groupName: string;
  groupCode: string;
  displayName: string;
}

const CreateGroups: React.FC = () => {
  const preLanguage = localStorage.getItem('language');
  const companyId = localStorage.getItem('companyId');
  const userId = localStorage.getItem('userId');
  const [language, setLanguage] = useState<any>(null);
  const [formValues, setFormValues] = useState<TermsResponse>({
    groupName: '',
    groupCode: '',
    displayName: '',
  });
  const [errors, setErrors] = useState<any>({});
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const { isMobile, getWidth } = useResponsive();
  const path = window.location.pathname;
  const id: string = path.split('/').pop() || '';
  const limits = useFieldLimits(inventoryFormLimits('product-group'));
  const kb = useFormKeyboard({ autoFocus: id === 'new' && !isMobile });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormValues(prevValues => ({
      ...prevValues,
      [name]: value,
    }));
  };
  useEffect(() => {
    if (id !== 'new') {
      fetchData(id);
    }
    const handleNewToken = async () => {
      await fetchData(id);
    };
    document.addEventListener('newToken', handleNewToken);
    return () => {
      document.removeEventListener('newToken', handleNewToken);
    };
  }, [path]);
  const fetchData = async (id: string) => {
    webLoadingTrue();
    try {
      const response: any = await getDetailProductGroup(id, companyId);
      console.log('🚀 ~ fetchData ~ response:', response);
      setFormValues(response.data);
    } catch (error) {
      console.error('Error loading terms:', error);
    } finally {
      webLoadingFalse();
    }
  };
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);
  const handleClosePopup = () => {
    setShowPopup(false);
  };
  const handleOpenPopup = () => {
    setShowPopup(true);
  };
  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    const newErrors: any = validateAgainstLimits({ ...formValues }, limits, language, ['groupCode', 'groupName', 'displayName']);
    if (!formValues.groupName) {
      newErrors.groupName = language?.['Group name is required'] ?? 'Group name is required';
    }
    if (!formValues.groupCode) {
      newErrors.groupCode = language?.['Group code is required'] ?? 'Group code is required';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    webLoadingTrue();
    const payload: any = { ...formValues, companyId, userId };
    if (id === 'new') {
      try {
        const response: any = await createGroupProduct(payload);
        if (response?.data?.id) {
          webNavigate('/' + preLanguage + '/items' + '?tab=Groups');
          webLoadingFalse();
          showToast({
            message: 'Item Group Created !',
            type: 'success',
            duration: 20000,
          });
        }
      } catch (error: any) {
        webLoadingFalse();
        showApiError(error, { language, setFieldErrors: fe => setErrors((prev: any) => ({ ...prev, ...fe })) });
      }
    } else {
      try {
        const response: any = await updateGroupProduct(id, payload);
        if (response?.data?.id) {
          webNavigate('/' + preLanguage + '/items' + '?tab=Groups');
          webLoadingFalse();
          showToast({
            message: language?.['Item Group Updated !'] ?? 'Item Group Updated !',
            type: 'success',
            duration: 20000,
          });
        }
      } catch (error: any) {
        webLoadingFalse();
        showApiError(error, { language, setFieldErrors: fe => setErrors((prev: any) => ({ ...prev, ...fe })) });
      }
    }
  };

  return (
    <div {...kb.scopeProps}>
      <div className="bg-white">
        <div className="flex items-center ">
          <div
            className="w-6 h-6 flex items-center justify-center cursor-pointer"
            onClick={() => {
              webNavigate('/' + preLanguage + '/items' + '?tab=Groups');
            }}
          >
            <BackIcon />
          </div>
          <p
            style={{ fontSize: '14px', fontWeight: '500', lineHeight: '28px', color: '#222525' }}
            className="font-inter"
          >
            {language && language?.['Back'] ? language?.['Back'] : 'Back'}
          </p>
        </div>
      </div>
      {id === 'new' ? (
        <p
          style={{
            margin: '20px 0px',
            fontSize: '20px',
            fontWeight: '500',
            lineHeight: '28px',
            color: '#222525',
          }}
          className="font-inter"
        >
          {language && language?.['Add Items group details']
            ? language?.['Add Items group details']
            : 'Add Items group details'}
        </p>
      ) : (
        <p
          style={{
            margin: '20px 0px',
            fontSize: '20px',
            fontWeight: '500',
            lineHeight: '28px',
            color: '#222525',
          }}
          className="font-inter"
        >
          {language && language?.['Edit Items group details']
            ? language?.['Edit Items group details']
            : 'Edit Items group details'}
        </p>
      )}
      <form
        className="scroll-container"
        onSubmit={handleSubmit}
        style={{ height: 'calc(100% - 163px)', overflowY: 'scroll' }}
      >
        <div className="flex items-start justify-between" style={{ gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ width: isMobile ? '100%' : 'calc(33% - 12px)' }}>
            <div className="space-y-2">
              <div className="flex items-center ">
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#4A5353' }}>
                  {language && language?.['Group Items code']
                    ? language?.['Group Items code']
                    : 'Group Items code'}
                </p>
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>
                  {language && language?.['*'] ? language?.['*'] : '*'}
                </p>
              </div>
              <div className="w-full">
                <input
                  required
                  type="text"
                  name="groupCode"
                  {...limitProps(limits, 'groupCode')}
                  value={formValues.groupCode} // Use groupCode instead of formValues?.groupCode
                  onChange={handleChange}
                  className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                />
                <FieldCounter value={formValues.groupCode} max={limits.groupCode?.maxLength} />
              </div>
              {errors.groupCode && <p className="error">{errors.groupCode}</p>}
            </div>
          </div>
          <div style={{ width: isMobile ? '100%' : 'calc(33% - 12px)' }}>
            <div className="space-y-2">
              <div className="flex items-center ">
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#4A5353' }}>
                  {language && language?.['Group Items name']
                    ? language?.['Group Items name']
                    : 'Group Items name'}
                </p>
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>
                  {language && language?.['*'] ? language?.['*'] : '*'}
                </p>
              </div>
              <div className="w-full">
                <input
                  required
                  type="text"
                  name="groupName"
                  {...limitProps(limits, 'groupName')}
                  value={formValues.groupName}
                  onChange={handleChange}
                  className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                />
                <FieldCounter value={formValues.groupName} max={limits.groupName?.maxLength} />
              </div>
              {errors.groupName && <p className="error">{errors.groupName}</p>}
            </div>
          </div>
          <div style={{ width: isMobile ? '100%' : 'calc(33% - 12px)' }}>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#4A5353' }}>
                  {language && language?.['Display name']
                    ? language?.['Display name']
                    : 'Display name'}
                </p>
              </div>
              <div className="w-full">
                <input
                  required
                  type="text"
                  name="displayName"
                  {...limitProps(limits, 'displayName')}
                  value={formValues.displayName} // Use displayName instead of formValues?.displayName
                  onChange={handleChange}
                  className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                />
                <FieldCounter value={formValues.displayName} max={limits.displayName?.maxLength} />
              </div>
              {errors.displayName && <p className="error">{errors.displayName}</p>}
            </div>
          </div>
        </div>
      </form>
      <div
        className="w-full flex items-center justify-end shadow_button relative"
        style={{ gap: "12px", paddingTop: "24px", }}
      >
        <div className="absolute" style={{ left: "-24px", top: 0, height: "1px", backgroundColor: "#DBE4E4", width: isMobile ? 'calc(100vw + 32px)' : "calc(-256px + 48px + 100vw)" }}></div>
        <button
          type="button"
          onClick={() => {
            webNavigate('/' + preLanguage + '/items' + '?tab=Groups');
          }}
          style={{
            padding: '10px 24px',
            borderRadius: '30px',
            color: '#222525',
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid #C9D7D8',
          }}
          className="font-inter cursor-pointer"
        >
          {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
        </button>
        <button
          type="button"
          {...kb.primaryProps(handleSubmit)}
          style={{
            padding: '10px 24px',
            borderRadius: '30px',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '500',
            backgroundColor: '#0F7679',
          }}
          className="font-inter cursor-pointer"
        >
          {language && language?.['Save'] ? language?.['Save'] : 'Save'}
        </button>
      </div>
      <PopupLocation
        show={showPopup}
        title={`${id === 'new'
          ? language && language?.['Create Group Tax']
            ? language?.['Create Group Tax']
            : 'Create Group Tax'
          : language && language?.['Update Group Tax']
            ? language?.['Update Group Tax']
            : 'Update Group Tax'
          }`}
        desc={`${id === 'new'
          ? language && language?.['Create Group Tax Failed']
            ? language?.['Create Group Tax Failed']
            : 'Create Group Tax Failed'
          : language && language?.['Update Group Tax Failed']
            ? language?.['Update Group Tax Failed']
            : 'Update Group Tax Failed'
          }`}
        onClose={handleClosePopup}
        onOpen={handleOpenPopup}
      />
    </div>
  );
};

export default CreateGroups;
